/**
 * Virtualized data grid. Sticky RTL header, frozen leading record-id column,
 * click-to-sort (server-sortable columns), row drilldown, resizable + auto-size
 * columns (drag the header edge; double-click it to fit content).
 *
 * Server pagination keeps the row set per page bounded; row virtualization keeps
 * even large page sizes smooth.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowUp, ArrowDown, Inbox } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { EmptyState } from '@/components/shared/empty-state'
import { ExplorerCell } from './explorer-cell'
import { cellText } from '../lib/cell-text'
import type { ResolvedColumn, ResolvedRow } from '../lib/spec-resolver'
import type { ViewSort } from '../types/data-view-spec'

const ROW_HEIGHT = 41
const DEFAULT_W = 180
const MIN_W = 80
const MAX_W = 640

interface ExplorerGridProps {
  columns: ResolvedColumn[]
  rows: ResolvedRow[]
  loading?: boolean
  sort: ViewSort | null
  sortMap: Record<string, string>
  onSort: (sortKey: string) => void
  onRowClick?: (row: ResolvedRow) => void
  onOpenArray?: (column: ResolvedColumn, row: ResolvedRow) => void
}

// Approximate text width for auto-size (canvas measured once, font ~ text-sm Vazirmatn).
let measureCtx: CanvasRenderingContext2D | null = null
function textWidth(s: string): number {
  if (!measureCtx) {
    const c = document.createElement('canvas')
    measureCtx = c.getContext('2d')
    if (measureCtx) measureCtx.font = '14px Vazirmatn, system-ui, sans-serif'
  }
  return measureCtx ? measureCtx.measureText(s).width : s.length * 8
}

export function ExplorerGrid({
  columns, rows, loading, sort, sortMap, onSort, onRowClick, onOpenArray,
}: ExplorerGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [widths, setWidths] = useState<Record<string, number>>({})
  const drag = useRef<{ id: string; startX: number; startW: number; rtl: boolean } | null>(null)

  const widthOf = (id: string) => widths[id] ?? DEFAULT_W

  const autoSize = useCallback((col: ResolvedColumn) => {
    let max = textWidth(col.label) + 28
    // sample up to 200 rows for speed
    for (let i = 0; i < Math.min(rows.length, 200); i++) {
      const w = textWidth(cellText(col, rows[i])) + 32
      if (w > max) max = w
    }
    setWidths((prev) => ({ ...prev, [col.id]: Math.max(MIN_W, Math.min(MAX_W, Math.round(max))) }))
  }, [rows])

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current
      if (!d) return
      const delta = d.rtl ? d.startX - e.clientX : e.clientX - d.startX
      const w = Math.max(MIN_W, Math.min(MAX_W, d.startW + delta))
      setWidths((prev) => ({ ...prev, [d.id]: w }))
    }
    const onUp = () => { drag.current = null; document.body.style.cursor = '' }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp) }
  }, [])

  const startResize = (e: React.PointerEvent, id: string) => {
    e.preventDefault(); e.stopPropagation()
    const rtl = getComputedStyle(scrollRef.current ?? document.body).direction === 'rtl'
    drag.current = { id, startX: e.clientX, startW: widthOf(id), rtl }
    document.body.style.cursor = 'col-resize'
  }

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  })

  if (!loading && rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState icon={Inbox} title="داده‌ای برای نمایش نیست"
          description="هنوز رکوردی برای این فرم ثبت نشده یا فیلترها نتیجه‌ای ندارند." />
      </div>
    )
  }

  const gridTemplate = columns.map((c) => `${widthOf(c.id)}px`).join(' ')
  const items = virtualizer.getVirtualItems()

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto relative">
      <div className="min-w-max">
        {/* Header */}
        <div className="grid sticky top-0 z-20 bg-muted border-b border-border" style={{ gridTemplateColumns: gridTemplate }}>
          {columns.map((col, i) => {
            const sortKey = sortMap[col.id]
            const sortable = !!sortKey
            const active = sort?.key === sortKey
            return (
              <div key={col.id} className={cn('relative flex items-center border-e border-border/50', i === 0 && 'sticky inset-inline-start-0 z-10 bg-muted')}>
                <button
                  type="button"
                  disabled={!sortable}
                  onClick={() => sortable && onSort(sortKey)}
                  className={cn('flex-1 flex items-center gap-1 px-4 py-2.5 text-xs font-semibold text-muted-foreground text-start whitespace-nowrap overflow-hidden',
                    sortable && 'hover:text-foreground cursor-pointer')}
                  title={sortable ? 'مرتب‌سازی' : undefined}
                >
                  <span className="truncate">{col.label}</span>
                  {active && (sort?.dir === 'asc'
                    ? <ArrowUp className="w-3 h-3 shrink-0 text-primary" />
                    : <ArrowDown className="w-3 h-3 shrink-0 text-primary" />)}
                </button>
                {/* resize handle on the column's TRAILING edge (inline-end) — the same
                    side as the visible `border-e` divider, so the grab point matches what
                    the user sees. Correct in both RTL and LTR. Dbl-click to auto-fit. */}
                <div
                  onPointerDown={(e) => startResize(e, col.id)}
                  onDoubleClick={() => autoSize(col)}
                  title="کشیدن برای تغییر عرض · دوبار کلیک برای اندازهٔ خودکار"
                  className="absolute inset-inline-end-0 top-0 h-full w-2 cursor-col-resize hover:bg-primary/40 z-10"
                />
              </div>
            )
          })}
        </div>

        {/* Body */}
        {loading ? (
          <div>
            {Array.from({ length: 8 }).map((_, r) => (
              <div key={r} className="grid border-b border-border" style={{ gridTemplateColumns: gridTemplate }}>
                {columns.map((c) => <div key={c.id} className="px-4 py-3"><div className="h-4 bg-muted rounded animate-pulse" /></div>)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {items.map((vi) => {
              const row = rows[vi.index]
              return (
                <div
                  key={row.id}
                  className={cn('grid border-b border-border hover:bg-muted/40 transition-colors absolute w-full', onRowClick && 'cursor-pointer')}
                  style={{ gridTemplateColumns: gridTemplate, transform: `translateY(${vi.start}px)`, height: ROW_HEIGHT }}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col, i) => (
                    <div key={col.id} className={cn('px-4 flex items-center text-sm text-foreground truncate border-e border-border/30',
                      i === 0 && 'sticky inset-inline-start-0 z-10 bg-background')}>
                      <ExplorerCell column={col} row={row} onOpenArray={onOpenArray} />
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
